import { Plus, Maximize2, Save } from "lucide-react";
import { useState, useRef, useCallback, useEffect, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DeclarationDocumentsTableContent } from './DeclarationDocumentsTableContent';
import { setLastEditedDocumentField, clearLastEditedDocumentField } from '../../../store/reducers/index';
import { getDeclarationSummaryTranslation } from '../../../hooks/useLanguage';
import { useDebouncedUpdate } from '../../../hooks/useDebouncedUpdate';

const DeclarationDocumentsTableComponent = ({
  declarationIndex,
  isSpanish,
  declaration,
  editableData,
  setEditableData,
  setIsDirty,
}) => {
  const [openDocModalIndex, setOpenDocModalIndex] = useState(null);
  const [openTipoModal, setOpenTipoModal] = useState({});
  const [isExpandModalOpen, setIsExpandModalOpen] = useState(false);
  const dispatch = useDispatch();
  const company = useSelector((state) => state.company);
  const userData = useSelector((state) => state.user.userData);
  const companyName = company?.nombre?.toLowerCase() || userData?.first_name?.toLowerCase();
  
  const { 
    lastEditedDocumentField,
    lastEditedDocumentDeclarationIndex,
    lastEditedDocumentPartidaIndex,
    lastEditedDocumentDocumentIndex 
  } = useSelector(state => ({
    lastEditedDocumentField: state.documents.lastEditedDocumentField,
    lastEditedDocumentDeclarationIndex: state.documents.lastEditedDocumentDeclarationIndex,
    lastEditedDocumentPartidaIndex: state.documents.lastEditedDocumentPartidaIndex,
    lastEditedDocumentDocumentIndex: state.documents.lastEditedDocumentDocumentIndex,
  }), (prev, next) => 
    prev.lastEditedDocumentField === next.lastEditedDocumentField &&
    prev.lastEditedDocumentDeclarationIndex === next.lastEditedDocumentDeclarationIndex &&
    prev.lastEditedDocumentPartidaIndex === next.lastEditedDocumentPartidaIndex &&
    prev.lastEditedDocumentDocumentIndex === next.lastEditedDocumentDocumentIndex
  );
  
  const editableDataRef = useRef(editableData);
  
  useEffect(() => {
    editableDataRef.current = editableData;
  }, [editableData]);
  
  const canShowApplyButton = 
    lastEditedDocumentField &&
    lastEditedDocumentDeclarationIndex === declarationIndex &&
    lastEditedDocumentPartidaIndex === null &&
    lastEditedDocumentDocumentIndex !== null &&
    declaration.documentos?.length > 1;
  
  // debounced update callback
  const handleUpdate = useCallback((updatedData, field, value, docIndex) => {
    setEditableData(updatedData);
    setIsDirty(true);
    dispatch(setLastEditedDocumentField({
      field,
      value,
      declarationIndex,
      partidaIndex: null,
      documentIndex: docIndex,
    }));
  }, [dispatch, setEditableData, setIsDirty, declarationIndex]);
  
  const { cancelPendingUpdates } = useDebouncedUpdate(
    (updatedData, field, value, docIndex) => handleUpdate(updatedData, field, value, docIndex),
    300
  );
  
  const handleApplyToAll = useCallback(() => {
    if (!canShowApplyButton || lastEditedDocumentDocumentIndex === null) return;
    
    const current = editableDataRef.current;
    const sourceDoc = current?.[declarationIndex]?.documentos?.[lastEditedDocumentDocumentIndex];
    if (!sourceDoc || !lastEditedDocumentField) return;
    
    // cancel any pending debounced updates
    cancelPendingUpdates();
    
    const updated = current.map((decl, dIdx) => 
      dIdx === declarationIndex ? {
        ...decl,
        documentos: decl.documentos.map((doc, idx) => 
          idx !== lastEditedDocumentDocumentIndex ? { ...doc, [lastEditedDocumentField]: sourceDoc[lastEditedDocumentField] } : doc
        )
      } : decl
    );
    
    editableDataRef.current = updated;
    setEditableData(updated);
    setIsDirty(true);
    dispatch(clearLastEditedDocumentField());
  }, [canShowApplyButton, lastEditedDocumentDocumentIndex, lastEditedDocumentField, declarationIndex, setEditableData, setIsDirty, dispatch, cancelPendingUpdates]);

  // Handler for adding Y025 document for TuDeFrigo
  const handleAddY025TuDeFrigo = () => {
    const updatedData = [...editableData];
    if (!updatedData[declarationIndex].documentos)
      updatedData[declarationIndex].documentos = [];
    updatedData[declarationIndex].documentos.push({
      codigo_documento: 'Y025',
      tipo: 1,
      numero: 'ESAEOF240000022A',
      fecha: '2025-10-30',
    });
    setEditableData(updatedData);
    setIsDirty(true);
    dispatch(setLastEditedDocumentField({
      field: '__declaration_documents__',
      value: null,
      declarationIndex,
      partidaIndex: null, // null for declaration-level
      documentIndex: updatedData[declarationIndex].documentos.length - 1,
    }));
  };

  // Handler for adding Y025 document for El Mosca
  const handleAddY025ElMosca = () => {
    const updatedData = [...editableData];
    if (!updatedData[declarationIndex].documentos)
      updatedData[declarationIndex].documentos = [];
    updatedData[declarationIndex].documentos.push({
      codigo_documento: 'Y025',
      tipo: 1,
      numero: 'ESAEOF240000022A',
      fecha: '2025-10-30',
    });
    setEditableData(updatedData);
    setIsDirty(true);
    dispatch(setLastEditedDocumentField({
      field: '__declaration_documents__',
      value: null,
      declarationIndex,
      partidaIndex: null,
      documentIndex: updatedData[declarationIndex].documentos.length - 1,
    }));
  };

  if (!declaration.documentos || !Array.isArray(declaration.documentos) || declaration.documentos.length === 0) {
    return null;
  }

  return (
    <div className="document-table mt-6 ">
      <div className="form-group flex-1 mt-12 !mb-0 flex justify-between flex-row items-center">
        <label htmlFor="companyName" className="!mb-0 !text-sm !opacity-100 !text-xl font-semibold uppercase">
          {!isSpanish ? "Export Level Documents" : "Documentos Nivel Exportación"} (
          {declaration.documentos?.length || 0})
        </label>
        <div className="flex gap-2 items-center">
          {/* Default add document button */}
          <button
            className="py-1 text-xs flex items-center gap-1"
            onClick={() => {
              const updatedData = [...editableData];
              if (!updatedData[declarationIndex].documentos)
                updatedData[declarationIndex].documentos = [];
              updatedData[declarationIndex].documentos.push({
                codigo_documento: "",
                tipo: "",
                numero: "",
                fecha: "",
              });
              setEditableData(updatedData);
              setIsDirty(true);
              dispatch(setLastEditedDocumentField({
                field: '__declaration_documents__',
                value: null,
                declarationIndex,
                partidaIndex: null,
                documentIndex: updatedData[declarationIndex].documentos.length - 1,
              }));
            }}
          >
            <Plus className={`size-4`} />
            {isSpanish ? "Agregar Documento" : "Add Document"}
          </button>
          {/* Client-specific Y025 buttons */}
          {companyName === 'tudefrigo' && (
            <button
              className="py-1.5 text-xs flex items-center gap-1 bg-secondary rounded-full pl-2 pr-3"
              onClick={handleAddY025TuDeFrigo}
              type="button"
            >
              <Plus className="size-4" />
              Agregar Y025
            </button>
          )}
          {companyName === 'el mosca' && (
            <button
              className="py-1.5 text-xs flex items-center gap-1 bg-secondary rounded-full pl-2 pr-3"
              onClick={handleAddY025ElMosca}
              type="button"
            >
              <Plus className="size-4" />
              Agregar Y025
            </button>
          )}
          {/* Expand View button */}
          <button
            className="bg-default text-white rounded-full py-1.5 px-3 text-xs flex items-center gap-1"
            onClick={() => setIsExpandModalOpen(true)}
            title={!isSpanish ? "Expand View" : "Vista Expandida"}
          >
            <Maximize2 className="size-3 stroke-white" />
            {!isSpanish ? "Expand View" : "Vista Expandida"}
          </button>
          {canShowApplyButton && (
            <button
              className="py-2 text-xs flex items-center gap-2 rounded-full px-3 bg-primary disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleApplyToAll}
              title={getDeclarationSummaryTranslation('saveAllDocumentsTooltip', isSpanish)}
              type="button"
            >
              <Save className="size-4" />
              <span className="text-xs">{getDeclarationSummaryTranslation('saveAllDocuments', isSpanish)}</span>
            </button>
          )}
        </div>
      </div>
      <DeclarationDocumentsTableContent
        declarationIndex={declarationIndex}
        isSpanish={isSpanish}
        declaration={declaration}
        editableData={editableData}
        setEditableData={setEditableData}
        setIsDirty={setIsDirty}
        openDocModalIndex={openDocModalIndex}
        setOpenDocModalIndex={setOpenDocModalIndex}
        openTipoModal={openTipoModal}
        setOpenTipoModal={setOpenTipoModal}
      />
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders Only re-render if props that affect THIS component changed
const areDeclarationDocumentsTablePropsEqual = (prevProps, nextProps) => {
  // Always re-render if these props change
  if (prevProps.declarationIndex !== nextProps.declarationIndex) return false;
  if (prevProps.isSpanish !== nextProps.isSpanish) return false;
  
  // Check if declaration prop changed (specifically documentos array)
  const prevDecl = prevProps.declaration;
  const nextDecl = nextProps.declaration;
  
  if (prevDecl === nextDecl) {
    // Check editableData for this declaration
    const prevDocumentos = prevProps.editableData?.[prevProps.declarationIndex]?.documentos;
    const nextDocumentos = nextProps.editableData?.[nextProps.declarationIndex]?.documentos;
    if (prevDocumentos !== nextDocumentos) return false;
    return true;
  }
  
  // Check if documentos array reference changed
  if (prevDecl?.documentos !== nextDecl?.documentos) return false;
  
  // Ignore function prop changes (they're recreated but functionally equivalent) setEditableData, setIsDirty are stable from parent
  
  return true; // Props are equal, skip re-render
};

export const DeclarationDocumentsTable = memo(DeclarationDocumentsTableComponent, areDeclarationDocumentsTablePropsEqual);

